import React from 'react';
import { QUALIFICATION_ZONES, calculateStandings } from '../utils/gameData';
import { ScrollArea } from './ui/scroll-area';
import { Trophy, Medal, Minus } from '@phosphor-icons/react';

export const Standings = ({ teams, onTeamClick }) => {
  const standings = calculateStandings(teams);

  const zoneFor = (position) => {
    if (position <= QUALIFICATION_ZONES.FIFA_CLUB_WC.end) {
      return { tag: 'cwc', color: '#FFB400', glow: 'rgba(255,180,0,0.35)', Icon: Trophy };
    }
    if (position <= QUALIFICATION_ZONES.ASIAN_CUP.end) {
      return { tag: 'asian', color: '#CCFF00', glow: 'rgba(204,255,0,0.3)', Icon: Medal };
    }
    return { tag: 'none', color: '#4A5278', glow: 'transparent', Icon: Minus };
  };

  return (
    <div
      data-testid="standings-table"
      className="rounded-xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0A0D1F 0%, #070A1A 100%)',
        border: '1px solid #1E2240',
        boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.35)',
      }}
    >
      {/* header strip */}
      <div className="px-5 py-4 border-b border-[#1E2240] flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-[0.22em]">
            League · Standings
          </h3>
          <p className="text-[11px] text-[#7A86B5] mt-0.5">
            Live table · tap any row for team details
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#4A5278]">
          <span>Season Table</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
        </div>
      </div>

      <ScrollArea className="h-[520px]">
        <table className="w-full border-collapse" style={{ fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-[#5A6390]">
              <th className="text-left pl-5 py-3 w-12 font-semibold">#</th>
              <th className="text-left pr-2 py-3 w-6"></th>
              <th className="text-left py-3 font-semibold">Team</th>
              <th className="text-center py-3 w-10 font-semibold">P</th>
              <th className="text-center py-3 w-10 font-semibold">W</th>
              <th className="text-center py-3 w-10 font-semibold">D</th>
              <th className="text-center py-3 w-10 font-semibold">L</th>
              <th className="text-center py-3 w-12 font-semibold">GF</th>
              <th className="text-center py-3 w-12 font-semibold">GA</th>
              <th className="text-center py-3 w-14 font-semibold">GD</th>
              <th className="text-center py-3 w-14 pr-5 font-semibold text-white">Pts</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((team, idx) => {
              const position = idx + 1;
              const gd = team.stats.goalsFor - team.stats.goalsAgainst;
              const z = zoneFor(position);
              return (
                <tr
                  key={team.id}
                  data-testid={`standings-row-${position}`}
                  onClick={() => onTeamClick?.(team)}
                  className="group cursor-pointer transition-all duration-150 hover:bg-[#101433]"
                  style={{
                    borderTop: '1px solid rgba(30,34,64,0.5)',
                  }}
                >
                  {/* position + zone bar */}
                  <td className="pl-5 py-3.5 relative">
                    {/* neon left bar for qualification zone */}
                    <span
                      aria-hidden
                      className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full"
                      style={{
                        background: z.tag !== 'none' ? z.color : 'transparent',
                        boxShadow: z.tag !== 'none' ? `0 0 10px ${z.glow}` : 'none',
                      }}
                    />
                    <span className="font-mono text-[13px] text-[#A0A8CC] font-medium">
                      {position}
                    </span>
                  </td>

                  {/* zone icon */}
                  <td className="pr-2 py-3.5">
                    <z.Icon
                      size={12}
                      weight="fill"
                      style={{ color: z.tag !== 'none' ? z.color : '#3A4278' }}
                    />
                  </td>

                  {/* team name */}
                  <td className="py-3.5 pr-3">
                    <span className="font-medium text-[14px] text-white group-hover:text-[#00E5FF] transition-colors truncate block max-w-[200px]">
                      {team.name}
                    </span>
                  </td>

                  <td className="text-center py-3.5 font-mono text-[13px] text-[#7A86B5]">{team.stats.played}</td>
                  <td className="text-center py-3.5 font-mono text-[13px] text-[#00FFA3] font-semibold">{team.stats.won}</td>
                  <td className="text-center py-3.5 font-mono text-[13px] text-[#7A86B5]">{team.stats.drawn}</td>
                  <td className="text-center py-3.5 font-mono text-[13px] text-[#FF4D6D]">{team.stats.lost}</td>
                  <td className="text-center py-3.5 font-mono text-[13px] text-[#C5CEEA]">{team.stats.goalsFor}</td>
                  <td className="text-center py-3.5 font-mono text-[13px] text-[#C5CEEA]">{team.stats.goalsAgainst}</td>
                  <td
                    className="text-center py-3.5 font-mono text-[13px] font-semibold"
                    style={{ color: gd > 0 ? '#00FFA3' : gd < 0 ? '#FF4D6D' : '#7A86B5' }}
                  >
                    {gd > 0 ? `+${gd}` : gd}
                  </td>
                  <td className="text-center py-3.5 pr-5">
                    <span className="font-mono text-[14px] font-bold text-white tabular-nums">
                      {team.stats.points}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollArea>

      {/* Legend */}
      <div className="border-t border-[#1E2240] px-5 py-3.5 flex flex-wrap items-center gap-5 text-[11px] text-[#7A86B5]">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#FFB400]" style={{ boxShadow: '0 0 8px rgba(255,180,0,0.6)' }} />
          <span className="tracking-wider">FIFA Club WC · 1 – 2</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#CCFF00]" style={{ boxShadow: '0 0 8px rgba(204,255,0,0.5)' }} />
          <span className="tracking-wider">Asian Cup · 1 – 4</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#4A5278]" />
          <span className="tracking-wider">No Continental · 5 – 20</span>
        </div>
      </div>
    </div>
  );
};
